import type { Book } from '@/types/domain'
import type { CloudBook } from '@/sync/cloud-index'

/**
 * Construye un `Book` local a partir de un `CloudBook` del índice de
 * Supabase y su `Blob` EPUB ya descargado.
 *
 * Reutiliza el **mismo id** que el libro tiene en la nube para que las
 * anotaciones sincronizadas por CRDT (room `book:<userId>:<bookId>`) y
 * el progreso encajen automáticamente entre dispositivos.
 *
 * Los datos que vienen de Supabase pueden traer referencias/proxies que
 * IndexedDB no puede clonar, así que se reconstruye un objeto plano puro
 * (structuredClone-friendly) con arrays saneados.
 *
 * @param cloud       Fila del índice cloud.
 * @param fileBlob    EPUB ya materializado en memoria.
 * @param coverBlob   Portada opcional. Si se omite (p. ej. en el camino
 *                    "leer directo" que difiere el parseo a background),
 *                    el libro se persiste sin portada y se rellena luego
 *                    vía `repo.saveBookCover`.
 * @param favorite    Si el libro es favorito según el set CRDT cross-device.
 *                    Por defecto `false`. El caller lo consulta desde
 *                    `useFavoritesStore` (mantener el mapper puro, sin
 *                    dependencias de Pinia).
 */
export function buildBookFromCloud(
  cloud: CloudBook,
  fileBlob: Blob,
  coverBlob?: Blob | null,
  favorite: boolean = false,
): Book {
  return {
    id: String(cloud.id),
    title: String(cloud.title),
    author: String(cloud.author ?? ''),
    coverBlob: coverBlob ?? undefined,
    fileBlob,
    metadata: {
      publisher: null,
      language: cloud.language ?? null,
      isbn: cloud.isbn ?? null,
      description: cloud.description ?? null,
      tags: Array.isArray(cloud.tags) ? cloud.tags.map((t) => String(t)) : [],
      collection: cloud.collection ?? null,
      seriesIndex: null,
      publishDate: null,
    },
    status: (['unread', 'reading', 'finished'].includes(cloud.status)
      ? cloud.status
      : 'unread') as Book['status'],
    progress: {
      cfi: cloud.progress_cfi ?? null,
      percent: Number(cloud.progress_percent) || 0,
      lastReadAt: null,
    },
    rating: Number(cloud.rating) || 0,
    favorite,
    dateAdded: new Date().toISOString(),
    dateFinished: null,
    shareId: null,
    contentHash: cloud.content_hash ?? undefined,
  }
}

/**
 * Versión "sin blob" del mapeo inverso: construye un `Book` a partir de un
 * `CloudBook` para representar en memoria un libro que está en la nube pero
 * que **aún no se ha descargado** a este dispositivo.
 *
 * A diferencia de `buildBookFromCloud`, no requiere (ni produce) el
 * `fileBlob`/`coverBlob`: el libro existe solo como metadatos para que la
 * Biblioteca unificada pueda listar, filtrar y ordenar títulos cloud junto
 * a los locales. Cuando el usuario lo abra o guarde offline, el flujo
 * `useCloudRead`/`useCloudDownload` descargará el blob y lo reemplazará por
 * la versión materializada.
 *
 * El campo `favorite` viene del set CRDT cross-device (`useFavoritesStore`),
 * no de `cloud_books` (que no lo guarda). El caller lo pasa para mantener el
 * mapper puro, sin dependencias de Pinia — mismo patrón que
 * `buildBookFromCloud`.
 *
 * `dateAdded` se rellena con `updated_at` del índice cloud como aproximación
 * del orden "recientes" en dispositivos que acaban de instalar la app y no
 * tienen el `dateAdded` real.
 */
export function cloudBookToBook(cloud: CloudBook, favorite: boolean = false): Book {
  const tags = Array.isArray(cloud.tags) ? cloud.tags.map((t) => String(t)) : []
  const status = (['unread', 'reading', 'finished'].includes(cloud.status)
    ? cloud.status
    : 'unread') as Book['status']
  return {
    id: String(cloud.id),
    title: String(cloud.title),
    author: String(cloud.author ?? ''),
    // Sin blobs: no está materializado en este dispositivo.
    metadata: {
      publisher: null,
      language: cloud.language ?? null,
      isbn: cloud.isbn ?? null,
      description: cloud.description ?? null,
      tags,
      collection: cloud.collection ?? null,
      seriesIndex: null,
      publishDate: cloud.publish_year ? String(cloud.publish_year) : null,
    },
    status,
    progress: {
      cfi: cloud.progress_cfi ?? null,
      percent: Number(cloud.progress_percent) || 0,
      lastReadAt: cloud.last_read_at ?? null,
    },
    rating: Number(cloud.rating) || 0,
    favorite,
    // Aproximación para el orden "recientes" en dispositivos sin datos locales.
    dateAdded: cloud.updated_at ?? new Date().toISOString(),
    dateFinished: cloud.date_finished ?? null,
    shareId: null,
    fileSize: cloud.file_size,
    contentHash: cloud.content_hash ?? undefined,
  }
}

/**
 * Inverso ligero de `cloudBookToBook`: reconstruye un `CloudBook` a partir
 * de un `Book` que llegó de la nube (sin blob). Sirve para pasar el libro
 * a `useCloudRead`/`useCloudDownload`, que esperan un `CloudBook`.
 *
 * Solo se debe usar con libros cuyo `Book` se originó en `cloudBookToBook`
 * (es decir, `cloudOnly`): para libros locales "de verdad" faltan campos
 * (p. ej. `user_id`) y no tendría sentido.
 */
export function bookToCloudBook(book: Book, userId: string): CloudBook {
  const publishYear = book.metadata.publishDate ? parseInt(book.metadata.publishDate.slice(0, 4), 10) : null
  return {
    id: book.id,
    user_id: userId,
    title: book.title,
    author: book.author,
    language: book.metadata.language,
    isbn: book.metadata.isbn,
    description: book.metadata.description,
    tags: book.metadata.tags ?? [],
    collection: book.metadata.collection,
    status: book.status,
    rating: book.rating,
    file_size: book.fileSize ?? 0,
    progress_cfi: book.progress.cfi,
    progress_percent: book.progress.percent,
    last_read_at: book.progress.lastReadAt ?? null,
    date_finished: book.dateFinished ?? null,
    publish_year: Number.isNaN(publishYear as number) ? null : publishYear,
    content_hash: book.contentHash ?? null,
    updated_at: book.dateAdded,
  }
}
